/* eslint-disable */
/* ═══════════════════════════════════════════════════════════
   FOOTY ANALYST — AI ENGINE
   Calls /api/claude serverless proxy (API key stays server-side)
   Squad Analyst · Opposition Scout · Player Dev · Session Builder · Video Narrative
═══════════════════════════════════════════════════════════ */

// Calls our own serverless proxy — never exposes the API key to the browser
const API_URL = '/api/claude';

/* ── Core API call with streaming support ── */
async function callClaude(systemPrompt, userMessage, onChunk = null) {
  const body = {
    system: systemPrompt,
    messages: [{ role: 'user', content: userMessage }],
    max_tokens: 1500,
    stream: !!onChunk,
  };

  const res = await fetch(API_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    let errMsg = `Server error ${res.status}`;
    try {
      const err = await res.json();
      errMsg = err.error || errMsg;
      if (err.detail) errMsg += ` — ${err.detail}`;
    } catch {}
    throw new Error(errMsg);
  }

  if (onChunk) {
    // Streaming — read SSE chunks
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let fullText = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      const chunk = decoder.decode(value, { stream: true });
      const lines = chunk.split('\n');

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        const data = line.slice(6).trim();
        if (data === '[DONE]') continue;
        try {
          const parsed = JSON.parse(data);
          // Handle Anthropic streaming format
          const delta =
            parsed?.delta?.text ||
            parsed?.content?.[0]?.text ||
            '';
          if (delta) {
            fullText += delta;
            onChunk(fullText);
          }
        } catch {}
      }
    }
    return fullText;
  } else {
    // Non-streaming
    const data = await res.json();
    return data?.content?.[0]?.text || '';
  }
}

/* ══════════════════════════════════════════════════
   1. SQUAD ANALYST
   Analyses the full squad and answers coaching questions
══════════════════════════════════════════════════ */
export async function analyseSquad(squad, question, onChunk) {
  const squadSummary = squad.map(p =>
    `${p.name} | ${p.pos} | ${p.style} | OVR:${p.overall} | PAC:${p.pace} SHO:${p.shooting} PAS:${p.passing} DRI:${p.dribbling} DEF:${p.defending} PHY:${p.physical}`
  ).join("\n");

  const system = `You are an elite football analyst and coaching consultant with UEFA Pro Licence expertise. You analyse squads with the precision of a Premier League analyst and the clarity of a great coach. You give specific, actionable advice — not generic platitudes.

When analysing a squad you consider:
- Position balance and squad depth at each position
- Attribute profiles and how they suit different tactical systems
- Player styles and how they complement each other
- Pressing capability, technical quality, physical presence
- Formation suitability based on the available personnel

Always be specific to the actual players and numbers given. Never give generic advice that could apply to any squad.
Format your response in clear sections. Be direct. Be tactical. Be genuinely useful.`;

  const user = `Here is the squad to analyse:

${squadSummary}

Coach's question: ${question}`;

  return callClaude(system, user, onChunk);
}

/* ══════════════════════════════════════════════════
   2. OPPOSITION SCOUT
   Generates a real tactical intelligence report
══════════════════════════════════════════════════ */
export async function generateOppositionReport(opponent, style, formation, threats, mySquad, onChunk) {
  const squadContext = mySquad.length > 0
    ? `\nOur squad: ${mySquad.slice(0,11).map(p=>`${p.name}(${p.pos})`).join(", ")}`
    : "";

  const system = `You are a professional football opposition analyst. You produce pre-match intelligence reports that are specific, tactical and immediately useful to a coach preparing for a match.

Your reports must:
- Be written for a real coach, not a fan
- Give specific tactical instructions, not vague advice
- Reference the opponent's style directly
- Include concrete instructions for the team
- Be structured clearly with sections
- Cover defensive and attacking phases separately
- End with a clear match day message

Never use filler phrases. Every sentence must give the coach something to work with.`;

  const user = `Generate a pre-match intelligence report for the following match:

OPPONENT: ${opponent}
THEIR STYLE: ${style}
THEIR FORMATION: ${formation}
KNOWN THREATS: ${threats.length > 0 ? threats.join(", ") : "None specified"}${squadContext}

Write a complete tactical dossier covering:
1. Their key principles and how they play
2. Their main threats and how to neutralise them
3. Our defensive instructions (specific to their style)
4. Our attacking instructions (how to hurt them)
5. Set piece considerations
6. Match day message to the team`;

  return callClaude(system, user, onChunk);
}

/* ══════════════════════════════════════════════════
   3. PLAYER DEVELOPMENT ADVISOR
   Personalised development plan for a specific player
══════════════════════════════════════════════════ */
export async function generatePlayerDevelopment(player, question, onChunk) {
  const system = `You are a specialist player development coach with 20 years experience developing professional footballers. You combine data-driven attribute analysis with practical coaching methods.

You give personalised, specific development advice based on the player's actual attribute profile, age, position and playing style. You reference their specific numbers. You recommend specific drills, training methods and tactical roles that will help them improve.

Age matters to your advice:
- Under 20: Focus on technical development and learning
- 20-26: Peak development window, tactical and physical
- 27-32: Maintain and refine
- 33+: Adapt game to compensate for physical decline

Always be specific to this player. Never give generic advice.`;

  const attrs = `PAC:${player.pace} SHO:${player.shooting} PAS:${player.passing} DRI:${player.dribbling} DEF:${player.defending} PHY:${player.physical}`;
  const user = `Player profile:
Name: ${player.name}
Position: ${player.pos} | Style: ${player.style}
Age: ${player.age} | Overall: ${player.overall} | Potential: ${player.potential}
Attributes: ${attrs}
${player.personality ? `Personality: ${player.personality}` : ""}
${player.proRef ? `Comparable to: ${player.proRef}` : ""}

${question || `What is this player's development path? What are their biggest improvement areas and how should they train?`}`;

  return callClaude(system, user, onChunk);
}

/* ══════════════════════════════════════════════════
   4. AI SESSION BUILDER
   Builds a complete training session from a brief
══════════════════════════════════════════════════ */
export async function buildTrainingSession(brief, squad, formation, onChunk) {
  const squadContext = squad.length > 0
    ? `\nSquad (${squad.length} players): ${squad.slice(0,15).map(p=>`${p.name}(${p.pos})`).join(", ")}`
    : "";

  const system = `You are a professional football head coach and session designer with UEFA Pro Licence. You design training sessions that are specific, progressive and match-realistic.

Every session you design must:
- Have a clear theme and objective
- Be structured as a proper session with warm-up, technical work, tactical work and finishing game
- Include specific drills with player numbers, dimensions and timing
- Have clear coaching points for each phase
- Be realistic for the squad size and level described
- Build progressively through the session

Format each drill clearly with: Name, Setup, Instructions, Coaching Points, Duration.`;

  const user = `Design a complete training session based on this brief:

BRIEF: ${brief}
FORMATION: ${formation || "Not specified"}${squadContext}

Create a full 75-90 minute session with 4-6 exercises that directly address the brief. Make it specific and immediately usable.`;

  return callClaude(system, user, onChunk);
}

/* ══════════════════════════════════════════════════
   5. VIDEO ANALYSIS NARRATIVE
   Intelligent interpretation of match data
══════════════════════════════════════════════════ */
export async function generateVideoNarrative(analysisData, matchData, onChunk) {
  const system = `You are a lead performance analyst at a professional football club. You write match analysis reports that coaches actually use — specific, clear, actionable.

When you receive match data you:
- Identify the most significant patterns and what they mean tactically
- Explain the pressing efficiency in the context of good/average/poor benchmarks
- Highlight what worked and what needs addressing
- Give 3-5 specific coaching recommendations with clear rationale
- Write in the language of a football coach, not a data scientist

Be direct. Be specific. Every sentence should give the coach something to think about or act on.`;

  const user = `Analyse this match performance data and write a coaching report:

MATCH: vs ${matchData.opponent || "Unknown"} | ${matchData.result || "Result TBC"} | ${matchData.competition || ""}

KEY METRICS:
- Pressing efficiency: ${analysisData.pressEfficiency}% (industry benchmark: 65-75% is good, 75%+ is elite)
- Possession: ${analysisData.possessionPct}%
- Pass accuracy: ${analysisData.passAccuracy}%
- High press wins: ${analysisData.highPressWins}
- Chances created: ${analysisData.chancesCreated}
- Shots on target: ${analysisData.shotsOnTarget}
- Distance covered: ${analysisData.distanceCovered}km

PRESSING ZONES:
${analysisData.pressingZones?.map(z=>`${z.zone}: ${z.pct}%`).join("\n") || "Not available"}

Write a match report with: Executive Summary, Key Patterns (what the data shows tactically), Strengths to Build On, Areas Requiring Attention, and 3 Priority Coaching Actions for next training.`;

  return callClaude(system, user, onChunk);
}

/* ══════════════════════════════════════════════════
   SHARED UI COMPONENTS
══════════════════════════════════════════════════ */

export const AI_CSS = `
/* ── AI CHAT CONTAINER ── */
.ai-wrap { font-family:'DM Sans',sans-serif; display:flex; flex-direction:column; height:100%; }
.ai-output {
  flex:1; overflow-y:auto;
  background:var(--white); border:1.5px solid var(--rule);
  padding:1.2rem; font-size:0.82rem; line-height:1.8;
  color:var(--ink2); font-weight:300;
  min-height:200px; max-height:600px;
  white-space:pre-wrap; word-break:break-word;
}
.ai-output h1,.ai-output h2,.ai-output h3 {
  font-family:'Bebas Neue',sans-serif; letter-spacing:2px;
  color:var(--ink); margin:0.75rem 0 0.35rem;
}
.ai-output h1 { font-size:1.2rem; }
.ai-output h2 { font-size:1rem; color:var(--teal); }
.ai-output h3 { font-size:0.88rem; color:var(--mustard2); }
.ai-output strong { font-weight:800; color:var(--ink); }
.ai-output em { font-style:italic; color:var(--teal2); }
.ai-output ul,.ai-output ol { padding-left:1.2rem; margin:0.4rem 0; }
.ai-output li { margin-bottom:0.3rem; }
.ai-output p { margin-bottom:0.6rem; }

/* Streaming cursor */
.ai-cursor { display:inline-block; width:2px; height:1em; background:var(--teal); margin-left:2px; animation:blink 0.7s infinite; vertical-align:text-bottom; }
@keyframes blink { 0%,100%{opacity:1} 50%{opacity:0} }

/* AI loading */
.ai-loading { display:flex; align-items:center; gap:0.5rem; padding:0.75rem; font-size:0.75rem; color:var(--muted); font-style:italic; }
.ai-loading-dot { width:7px; height:7px; border-radius:50%; background:var(--teal); animation:aiPulse 0.8s ease-in-out infinite alternate; }
.ai-loading-dot:nth-child(2) { animation-delay:0.2s; }
.ai-loading-dot:nth-child(3) { animation-delay:0.4s; }
@keyframes aiPulse { from{opacity:0.3;transform:scale(0.8)} to{opacity:1;transform:scale(1)} }

/* AI question input */
.ai-input-row { display:flex; gap:0.5rem; margin-top:0.75rem; }
.ai-input {
  flex:1; background:var(--cream); border:1.5px solid var(--rule);
  border-bottom:2px solid var(--teal); padding:0.65rem 0.9rem;
  font-family:'DM Sans',sans-serif; font-size:0.82rem; font-weight:500;
  color:var(--ink); outline:none;
}
.ai-input:focus { border-bottom-color:var(--ink); }
.ai-submit {
  padding:0.65rem 1.2rem; background:var(--teal); color:var(--white);
  border:2px solid var(--teal); font-family:'DM Sans',sans-serif;
  font-weight:800; font-size:0.72rem; letter-spacing:2px;
  text-transform:uppercase; cursor:pointer; transition:all 0.2s;
  white-space:nowrap;
}
.ai-submit:hover { background:var(--teal2); }
.ai-submit:disabled { opacity:0.5; cursor:not-allowed; }

/* AI powered badge */
.ai-badge {
  display:inline-flex; align-items:center; gap:0.4rem;
  font-size:0.6rem; font-weight:800; letter-spacing:2px;
  text-transform:uppercase; padding:0.14rem 0.55rem;
  background:linear-gradient(135deg,var(--teal2),var(--teal));
  color:var(--white); border-radius:2px;
}
.ai-badge::before { content:'✦'; font-size:0.6rem; }

/* Suggested questions */
.ai-suggestions { display:flex; gap:0.4rem; flex-wrap:wrap; margin-top:0.6rem; }
.ai-suggestion {
  font-size:0.68rem; font-weight:600; padding:0.32rem 0.75rem;
  border:1.5px solid var(--teal); color:var(--teal); background:transparent;
  cursor:pointer; transition:all 0.18s; font-family:'DM Sans',sans-serif;
}
.ai-suggestion:hover { background:var(--teal); color:var(--white); }

/* AI panel header */
.ai-panel-header {
  background:linear-gradient(135deg,var(--teal2),var(--teal));
  padding:1rem 1.2rem; border-bottom:2px solid var(--mustard);
  display:flex; align-items:center; justify-content:space-between;
}
.ai-panel-title { font-family:'Bebas Neue',sans-serif; font-size:1rem; letter-spacing:2.5px; color:var(--white); }
.ai-panel-sub { font-size:0.6rem; color:rgba(255,255,255,0.55); font-weight:300; margin-top:0.1rem; }

/* Response renderer */
.ai-response { }
.ai-response-section { margin-bottom:0.9rem; }
.ai-response-section-title {
  font-family:'Bebas Neue',sans-serif; font-size:0.82rem;
  letter-spacing:2px; color:var(--teal); margin-bottom:0.35rem;
  border-bottom:1px solid var(--teal3); padding-bottom:0.2rem;
}
.ai-response-text { font-size:0.79rem; color:var(--ink2); line-height:1.75; font-weight:300; }
.ai-response-bullet { display:flex; gap:0.5rem; margin-bottom:0.3rem; font-size:0.78rem; color:var(--ink2); line-height:1.55; }
.ai-response-bullet::before { content:'→'; color:var(--teal); font-weight:800; flex-shrink:0; }
`;

/* ── Simple markdown renderer ── */
export function AIResponse({ text, streaming = false }) {
  if (!text) return null;

  // Split into lines and render with basic markdown
  const lines = text.split('\n');
  const elements = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim() && i < lines.length - 1) { elements.push(<br key={i}/>); i++; continue; }
    if (line.startsWith('## ') || line.startsWith('### ')) {
      const level = line.startsWith('## ') ? 2 : 3;
      const txt = line.replace(/^#{2,3} /, '');
      elements.push(level === 2
        ? <div key={i} style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"0.92rem",letterSpacing:"2px",color:"var(--teal)",marginTop:"0.9rem",marginBottom:"0.3rem",borderBottom:"1px solid var(--teal3)",paddingBottom:"0.2rem"}}>{txt}</div>
        : <div key={i} style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"0.82rem",letterSpacing:"1.5px",color:"var(--mustard2)",marginTop:"0.6rem",marginBottom:"0.2rem"}}>{txt}</div>
      );
    } else if (line.startsWith('# ')) {
      elements.push(<div key={i} style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"1.1rem",letterSpacing:"2px",color:"var(--ink)",marginTop:"0.5rem",marginBottom:"0.4rem"}}>{line.slice(2)}</div>);
    } else if (line.startsWith('- ') || line.startsWith('• ') || line.startsWith('* ')) {
      elements.push(<div key={i} className="ai-response-bullet">{renderInline(line.slice(2))}</div>);
    } else if (/^\d+\. /.test(line)) {
      const txt = line.replace(/^\d+\. /, '');
      elements.push(<div key={i} className="ai-response-bullet">{renderInline(txt)}</div>);
    } else if (line.startsWith('**') && line.endsWith('**') && line.length > 4) {
      elements.push(<div key={i} style={{fontFamily:"'DM Sans',sans-serif",fontWeight:800,color:"var(--ink)",marginTop:"0.5rem",marginBottom:"0.2rem"}}>{line.slice(2,-2)}</div>);
    } else if (line.trim()) {
      elements.push(<div key={i} style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.79rem",color:"var(--ink2)",lineHeight:1.75,fontWeight:300,marginBottom:"0.3rem"}}>{renderInline(line)}</div>);
    }
    i++;
  }

  return (
    <div style={{padding:"0.5rem 0"}}>
      {elements}
      {streaming && <span className="ai-cursor"/>}
    </div>
  );
}

function renderInline(text) {
  // Handle **bold** and *italic* inline
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) return <strong key={i} style={{fontWeight:800,color:"var(--ink)"}}>{part.slice(2,-2)}</strong>;
    if (part.startsWith('*') && part.endsWith('*')) return <em key={i} style={{fontStyle:"italic",color:"var(--teal2)"}}>{part.slice(1,-1)}</em>;
    return part;
  });
}

/* ══════════════════════════════════════════════════
   PATTERN ENGINE
   Aggregates all season data and produces structured
   tactical intelligence — not a summary, a real analysis.
   Reads: fixtures, match analyses, player ratings, squad.
══════════════════════════════════════════════════ */

/* ── Compute all raw statistics from stored data ── */
export function computeSeasonPatterns(fixtures, analyses, playerRatings, squad) {
  const played = (fixtures||[]).filter(f => f.played && f.goalsFor !== undefined);
  const n = played.length;
  if (n === 0) return null;

  // ── RESULTS ──
  const wins   = played.filter(f => f.goalsFor > f.goalsAgainst);
  const draws  = played.filter(f => f.goalsFor === f.goalsAgainst);
  const losses = played.filter(f => f.goalsFor < f.goalsAgainst);
  const goalsFor = played.reduce((s,f) => s+(f.goalsFor||0), 0);
  const goalsAgainst = played.reduce((s,f) => s+(f.goalsAgainst||0), 0);

  // ── HOME VS AWAY SPLIT ──
  const home = played.filter(f => f.venue === "home");
  const away = played.filter(f => f.venue === "away");
  const homeWinPct = home.length ? Math.round(home.filter(f=>f.goalsFor>f.goalsAgainst).length/home.length*100) : null;
  const awayWinPct = away.length ? Math.round(away.filter(f=>f.goalsFor>f.goalsAgainst).length/away.length*100) : null;
  const homeGoalsFor  = home.length ? +(home.reduce((s,f)=>s+(f.goalsFor||0),0)/home.length).toFixed(2) : null;
  const awayGoalsFor  = away.length ? +(away.reduce((s,f)=>s+(f.goalsFor||0),0)/away.length).toFixed(2) : null;
  const homeGoalsAgainst = home.length ? +(home.reduce((s,f)=>s+(f.goalsAgainst||0),0)/home.length).toFixed(2) : null;
  const awayGoalsAgainst = away.length ? +(away.reduce((s,f)=>s+(f.goalsAgainst||0),0)/away.length).toFixed(2) : null;

  // ── TEMPORAL PATTERNS (goals by period) ──
  // Pull from match analyses moments
  const allMoments = (analyses||[]).flatMap(a => (a.moments||[]).map(m => ({...m, result:a.matchData?.goalsFor>a.matchData?.goalsAgainst?"W":a.matchData?.goalsFor<a.matchData?.goalsAgainst?"L":"D"})));
  const goalMoments     = allMoments.filter(m => m.type === "goal");
  const concernMoments  = allMoments.filter(m => m.type === "concern");
  const pressWinMoments = allMoments.filter(m => m.type === "press");

  // Split into 15-minute windows: 1-15, 16-30, 31-45, 46-60, 61-75, 76-90
  const windows = [[1,15],[16,30],[31,45],[46,60],[61,75],[76,90]];
  const goalsByWindow = windows.map(([from,to]) => ({
    label:  `${from}-${to}'`,
    goals:  goalMoments.filter(m => Number(m.time||0) >= from && Number(m.time||0) <= to).length,
    concerns: concernMoments.filter(m => Number(m.time||0) >= from && Number(m.time||0) <= to).length,
  }));

  // Most dangerous window for conceding
  const concernWindow = [...goalsByWindow].sort((a,b) => b.concerns - a.concerns)[0];
  // Best scoring window
  const scoringWindow = [...goalsByWindow].sort((a,b) => b.goals - a.goals)[0];

  // ── PRESS EFFICIENCY TREND ──
  const pressData = (analyses||[])
    .filter(a => a.pressEfficiency > 0)
    .sort((a,b) => new Date(a.matchData?.date||0) - new Date(b.matchData?.date||0));
  const avgPress = pressData.length ? +(pressData.reduce((s,a)=>s+a.pressEfficiency,0)/pressData.length).toFixed(1) : null;

  // Press in wins vs losses
  const pressInWins   = analyses?.filter(a=>a.pressEfficiency>0 && a.matchData?.goalsFor>a.matchData?.goalsAgainst).map(a=>a.pressEfficiency)||[];
  const pressInLosses = analyses?.filter(a=>a.pressEfficiency>0 && a.matchData?.goalsFor<a.matchData?.goalsAgainst).map(a=>a.pressEfficiency)||[];
  const avgPressWins   = pressInWins.length   ? +(pressInWins.reduce((s,v)=>s+v,0)/pressInWins.length).toFixed(1) : null;
  const avgPressLosses = pressInLosses.length ? +(pressInLosses.reduce((s,v)=>s+v,0)/pressInLosses.length).toFixed(1) : null;

  // Trend: compare first half of season vs second half
  const half = Math.floor(pressData.length/2);
  const pressFirstHalf  = half > 0 ? +(pressData.slice(0,half).reduce((s,a)=>s+a.pressEfficiency,0)/half).toFixed(1) : null;
  const pressSecondHalf = pressData.length-half > 0 ? +(pressData.slice(half).reduce((s,a)=>s+a.pressEfficiency,0)/(pressData.length-half)).toFixed(1) : null;

  // ── COMPETITION SPLIT ──
  const byComp = {};
  played.forEach(f => {
    const c = f.competition || "Unknown";
    if (!byComp[c]) byComp[c] = {played:0,wins:0,gf:0,ga:0};
    byComp[c].played++;
    if (f.goalsFor > f.goalsAgainst) byComp[c].wins++;
    byComp[c].gf += f.goalsFor||0;
    byComp[c].ga += f.goalsAgainst||0;
  });

  // ── SET PIECE PATTERNS ──
  const setpieceGoals    = goalMoments.filter(m => m.desc && /corner|free.?kick|set.?piece|header|penalty/i.test(m.desc));
  const setpieceConcerns = concernMoments.filter(m => m.desc && /corner|free.?kick|set.?piece|header|penalty/i.test(m.desc));
  const setpieceGoalPct  = goalMoments.length ? Math.round(setpieceGoals.length/goalMoments.length*100) : null;

  // ── POSSESSION CORRELATION ──
  const possData = (analyses||[]).filter(a => a.possessionPct > 0);
  const possWins  = possData.filter(a => a.matchData?.goalsFor > a.matchData?.goalsAgainst);
  const possLoss  = possData.filter(a => a.matchData?.goalsFor < a.matchData?.goalsAgainst);
  const avgPossWins = possWins.length  ? +(possWins.reduce((s,a)=>s+a.possessionPct,0)/possWins.length).toFixed(1)  : null;
  const avgPossLoss = possLoss.length  ? +(possLoss.reduce((s,a)=>s+a.possessionPct,0)/possLoss.length).toFixed(1) : null;

  // ── PLAYER PERFORMANCE PATTERNS ──
  const playerPatterns = {};
  (squad||[]).forEach(p => {
    const matchRecords = (playerRatings||[]).filter(m => m.ratings?.[p.id] > 0);
    if (matchRecords.length < 2) return;

    const ratings     = matchRecords.map(m => ({rating:m.ratings[p.id], result:m.result}));
    const avgRating   = +(ratings.reduce((s,r)=>s+r.rating,0)/ratings.length).toFixed(1);
    const ratingInWin = ratings.filter(r=>r.result==="Win").map(r=>r.rating);
    const ratingInLoss= ratings.filter(r=>r.result==="Loss").map(r=>r.rating);
    const avgInWin    = ratingInWin.length  ? +(ratingInWin.reduce((s,v)=>s+v,0)/ratingInWin.length).toFixed(1)  : null;
    const avgInLoss   = ratingInLoss.length ? +(ratingInLoss.reduce((s,v)=>s+v,0)/ratingInLoss.length).toFixed(1) : null;

    // Correlation: does their good performance predict a win?
    const highRatings = ratings.filter(r => r.rating >= 7);
    const winWhenHigh = highRatings.length ? Math.round(highRatings.filter(r=>r.result==="Win").length/highRatings.length*100) : null;

    playerPatterns[p.id] = {
      name: p.name, pos: p.pos,
      apps: matchRecords.length,
      avgRating, avgInWin, avgInLoss,
      winWhenHigh,
      // Form trend: last 3 vs previous 3
      recent:   +(ratings.slice(-3).reduce((s,r)=>s+r.rating,0)/Math.min(3,ratings.slice(-3).length)).toFixed(1),
      earlier:  ratings.length > 3 ? +(ratings.slice(0,-3).reduce((s,r)=>s+r.rating,0)/ratings.slice(0,-3).length).toFixed(1) : null,
    };
  });

  // ── KEY PERFORMERS ──
  const ranked = Object.values(playerPatterns)
    .filter(p => p.apps >= 3)
    .sort((a,b) => b.avgRating - a.avgRating);
  const topPerformers    = ranked.slice(0,3);
  const underperformers  = [...ranked].sort((a,b) => a.avgRating - b.avgRating).slice(0,2);

  // Most correlated with wins
  const impactPlayers = Object.values(playerPatterns)
    .filter(p => p.winWhenHigh !== null && p.apps >= 3)
    .sort((a,b) => b.winWhenHigh - a.winWhenHigh)
    .slice(0,3);

  // ── FORM STREAK ──
  const recent5 = played.slice(-5).map(f => f.goalsFor>f.goalsAgainst?"W":f.goalsFor<f.goalsAgainst?"L":"D");
  const currentStreak = (() => {
    if (!recent5.length) return null;
    const last = recent5[recent5.length-1];
    let count = 0;
    for (let i = recent5.length-1; i >= 0; i--) {
      if (recent5[i] === last) count++;
      else break;
    }
    return {type:last, count};
  })();

  // ── CLEAN SHEETS ──
  const cleanSheets = played.filter(f => f.goalsAgainst === 0).length;
  const failedToScore = played.filter(f => f.goalsFor === 0).length;

  return {
    // Overview
    n, wins: wins.length, draws: draws.length, losses: losses.length,
    winPct: Math.round(wins.length/n*100),
    goalsFor, goalsAgainst,
    goalsForPer90: +(goalsFor/n).toFixed(2),
    goalsAgainstPer90: +(goalsAgainst/n).toFixed(2),
    cleanSheets, failedToScore,
    currentStreak,
    recent5,

    // Venue split
    home: { n:home.length, winPct:homeWinPct, gfPer90:homeGoalsFor, gaPer90:homeGoalsAgainst },
    away: { n:away.length, winPct:awayWinPct, gfPer90:awayGoalsFor, gaPer90:awayGoalsAgainst },

    // Temporal
    goalsByWindow, scoringWindow, concernWindow,
    totalTaggedGoals: goalMoments.length,
    totalTaggedConcerns: concernMoments.length,
    totalPressWins: pressWinMoments.length,

    // Pressing
    avgPress, avgPressWins, avgPressLosses,
    pressFirstHalf, pressSecondHalf,
    pressMatches: pressData.length,

    // Possession
    avgPossWins, avgPossLoss,
    possMatches: possData.length,

    // Set pieces
    setpieceGoalPct, setpieceGoals: setpieceGoals.length, setpieceConcerns: setpieceConcerns.length,

    // Competition
    byComp,

    // Players
    playerPatterns, topPerformers, underperformers, impactPlayers,
    ratedMatches: (playerRatings||[]).length,
  };
}

/* ── Format patterns into a dense prompt for Claude ── */
function buildPatternPrompt(patterns, clubName, profile) {
  const {
    n, wins, draws, losses, winPct, goalsFor, goalsAgainst,
    goalsForPer90, goalsAgainstPer90, cleanSheets, failedToScore,
    currentStreak, recent5,
    home, away,
    goalsByWindow, scoringWindow, concernWindow,
    avgPress, avgPressWins, avgPressLosses, pressFirstHalf, pressSecondHalf, pressMatches,
    avgPossWins, avgPossLoss,
    setpieceGoalPct, setpieceGoals, setpieceConcerns,
    byComp, topPerformers, underperformers, impactPlayers,
  } = patterns;

  const streakStr = currentStreak
    ? `Current streak: ${currentStreak.count} ${currentStreak.type === "W" ? "wins" : currentStreak.type === "L" ? "losses" : "draws"} in a row`
    : "";

  const windowStr = goalsByWindow.map(w => `${w.label}: ${w.goals} goals scored, ${w.concerns} concerns`).join(" | ");

  const compStr = Object.entries(byComp).map(([c,d]) =>
    `${c}: P${d.played} W${d.wins} GF${d.gf} GA${d.ga}`
  ).join(" | ");

  const topStr = topPerformers.map(p =>
    `${p.name}(${p.pos}) avg:${p.avgRating}${p.avgInWin?` win:${p.avgInWin}`:""}`
  ).join(", ");

  const impactStr = impactPlayers.map(p =>
    `${p.name}: wins ${p.winWhenHigh}% of matches when rated 7+`
  ).join(", ");

  const underStr = underperformers.map(p =>
    `${p.name}(${p.pos}) avg:${p.avgRating}`
  ).join(", ");

  return `You are a UEFA Pro Licence performance analyst. Analyse these season statistics for ${clubName||"this club"} and identify the 5-7 most significant tactical and performance patterns. Be specific, direct and actionable. Reference the actual numbers. Prioritise patterns that explain WHY results happen — not just what happened.

SEASON OVERVIEW (${n} matches played):
Record: W${wins} D${draws} L${losses} (${winPct}% win rate)
Goals: ${goalsFor} scored (${goalsForPer90}/game) · ${goalsAgainst} conceded (${goalsAgainstPer90}/game)
Clean sheets: ${cleanSheets}/${n} · Failed to score: ${failedToScore}/${n}
${streakStr}
Recent form: ${(recent5||[]).join(" ")}

VENUE SPLIT:
Home (${home.n} games): ${home.winPct}% win rate · ${home.gfPer90} goals/game scored · ${home.gaPer90} goals/game conceded
Away (${away.n} games): ${away.winPct}% win rate · ${away.gfPer90} goals/game scored · ${away.gaPer90} goals/game conceded

COMPETITION SPLIT:
${compStr}

GOAL TIMING (from tagged moments across all matches):
${windowStr}
Best scoring window: ${scoringWindow?.label} (${scoringWindow?.goals} goals tagged)
Most concerning window: ${concernWindow?.label} (${concernWindow?.concerns} incidents)
Set piece goals: ${setpieceGoalPct !== null ? `${setpieceGoalPct}% of tagged goals came from set pieces` : "not enough data"}
${setpieceConcerns > 0 ? `Set piece vulnerabilities tagged: ${setpieceConcerns} times` : ""}

PRESSING DATA (${pressMatches} matches with data):
${avgPress !== null ? `Season average: ${avgPress}%` : "No press data yet"}
${avgPressWins !== null ? `When winning: ${avgPressWins}% · When losing: ${avgPressLosses}%` : ""}
${pressFirstHalf !== null ? `First half of season: ${pressFirstHalf}% → Second half: ${pressSecondHalf}% (${Number(pressSecondHalf) > Number(pressFirstHalf) ? "improving" : "declining"})` : ""}

POSSESSION CORRELATION:
${avgPossWins !== null ? `Average possession in wins: ${avgPossWins}% · In losses: ${avgPossLoss}%` : "Not enough data"}

PLAYER PERFORMANCE PATTERNS:
Top performers (avg rating across season): ${topStr||"No rating data yet"}
Impact players (correlation with wins): ${impactStr||"Need more rated matches"}
Underperforming: ${underStr||"No data yet"}

INSTRUCTIONS:
Write 5-7 numbered findings. Each finding must:
1. Name the specific pattern with the supporting numbers
2. Explain the likely tactical cause (not just what, but why)
3. Give one specific actionable recommendation

Format each as:
**[Finding title]**
[2-3 sentences: the data, the cause, the recommendation]

Prioritise findings by impact on results. Be direct. No padding.`;
}

/* ── Main exported function ── */
export async function runPatternEngine(fixtures, analyses, playerRatings, squad, clubName, profile, onChunk) {
  const patterns = computeSeasonPatterns(fixtures, analyses, playerRatings, squad);
  if (!patterns) throw new Error("Not enough data — log at least one match result to run the pattern engine.");

  const prompt = buildPatternPrompt(patterns, clubName, profile);

  const system = `You are a professional football performance analyst. You identify patterns in data and translate them into specific, actionable coaching intelligence. You write for managers — not academics. Be direct, specific and immediately useful.`;

  return { patterns, analysis: await callClaude(system, prompt, onChunk) };
}
